import React from "react";
import { Button } from "@/components/ui/button";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import { Coins, Gift, Check, Loader2, Gem, Spade, History } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";

interface AwardNotificationDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly awards: any[];
  readonly onClaimAll: () => void;
}

export function AwardNotificationDialog({
  open,
  onOpenChange,
  awards,
  onClaimAll,
}: AwardNotificationDialogProps) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const claimMutation = useMutation({
    mutationFn: async (awardId: number) => {
      const response = await apiRequest("POST", `/api/awards/player/awards/${awardId}/claim`);
      return response.json();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message ?? "Failed to claim award",
        variant: "destructive",
      });
    },
  });

  const handleClaimAll = async () => {
    try {
      // Claim all awards sequentially
      for (const award of awards) {
        const result = await claimMutation.mutateAsync(award.id);
        if (!result.success) {
          throw new Error(result.message ?? "Failed to claim award");
        }
      }
      
      // Invalidate the query to refresh the awards list
      await queryClient.invalidateQueries({ queryKey: ["player-awards-pending"] });
      
      toast({
        title: "Awards claimed",
        description: "All awards have been claimed successfully",
      });
      
      // Call the onClaimAll callback to update parent state
      onClaimAll();
      
      // Close the dialog
      onOpenChange(false);
      
    } catch (error) {
      console.error("Failed to claim all awards:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to claim all awards. Please try again.",
        variant: "destructive",
      });
      // Don't close the dialog on error so the user can try again
    }
  };

  const getAwardIcon = (award: any) => {
    switch (award.awardType) {
      case "CURRENCY":
        if (award.awardType === "CURRENCY" && award.currencyType === "GEMS") {
          return <Gem className="h-6 w-6 text-purple-400" />;
        }
        return <Coins className="h-6 w-6 text-yellow-500" />;
      case "DECK":
        return <Spade className="h-6 w-6 text-purple-500" />;
      case "SHOP_ITEM":
        return <Gift className="h-6 w-6 text-blue-500" />;
      default:
        return <Gift className="h-6 w-6" />;
    }
  };

  const getAwardDescription = (award: any) => {
    switch (award.awardType) {
      case "CURRENCY":
        return `${award.amount} ${award.currencyType === "GEMS" ? "Gems" : "Coins"}`;
      case "DECK":
        return `Deck: ${award.deck?.name ?? "Custom Deck"}`;
      case "SHOP_ITEM":
        return `Item: ${award.shopItem?.name ?? "Shop Item"}`;
      default:
        return "Special Award";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] bg-gradient-to-br from-blue-950 via-gray-900 to-black border-blue-500/30">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center flex items-center justify-center gap-2">
            <Gift className="h-6 w-6 text-yellow-500" />
            New Awards!
          </DialogTitle>
          <DialogDescription className="text-center">
            You have received the following awards:
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
          {awards.map((award) => (
            <div 
              key={award.id} 
              className="flex items-center space-x-4 p-3 rounded-lg border border-blue-500/20 bg-black/40 backdrop-blur-sm"
            >
              <div className="p-2 bg-blue-900/30 rounded-full">
                {getAwardIcon(award)}
              </div>
              <div className="flex-1">
                <h4 className="text-lg font-semibold">
                  {getAwardDescription(award)}
                </h4>
                {award.reason && (
                  <p className="text-sm text-gray-400">{award.reason}</p>
                )}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <div className="w-full space-y-2">
            <Button
              onClick={handleClaimAll}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={claimMutation.isPending || awards.length === 0}
            >
              {claimMutation.isPending ? (
                <span className="flex items-center">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Claiming...
                </span>
              ) : awards.length === 0 ? (
                <span className="flex items-center">
                  <Check className="h-4 w-4 mr-2" />
                  No Awards to Claim
                </span>
              ) : (
                <span className="flex items-center">
                  <Check className="h-4 w-4 mr-2" />
                  Claim All Rewards
                </span>
              )}
            </Button>
            
            <Button
              variant="outline"
              onClick={() => {
                setLocation('/all-awards');
                onOpenChange(false);
              }}
              className="w-full bg-transparent border-blue-500/50 text-blue-400 hover:bg-blue-500/10 hover:border-blue-500"
            >
              <History className="h-4 w-4 mr-2" />
              View All Awards
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
